import React, { useMemo, useRef, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, Plus, Clock, X, CheckCircle, XCircle, Pencil } from 'lucide-react';

// ─── Constantes ────────────────────────────────────────────────────────────────
const SLOT_HEIGHT   = 64;        // px par tranche de 30 min
const DAY_START_MIN = 7  * 60;   // 07h00
const DAY_END_MIN   = 21 * 60;   // 21h00

const timeToMinutes = (t) => {
  if (!t) return 0;
  const sep = t.includes('h') ? 'h' : ':';
  const [h, m] = t.split(sep).map(Number);
  return h * 60 + (m || 0);
};

const minutesToTime = (total) => {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
};

const SLOT_TIMES = (() => {
  const arr = [];
  for (let m = DAY_START_MIN; m < DAY_END_MIN; m += 30) arr.push(minutesToTime(m));
  return arr;
})();

const LEAVE_CONFIG = {
  'Maladie': { icon: '🤒', bg: 'bg-red-50/60',      badge: 'bg-red-100 text-red-500',      label: 'Maladie'  },
  'Congé':   { icon: '🌴', bg: 'bg-orange-50/50',   badge: 'bg-orange-100 text-orange-400', label: 'Congé'    },
  'Autre':   { icon: '🙏', bg: 'bg-purple-50/50',   badge: 'bg-purple-100 text-purple-500', label: 'Absence'  },
};

// Couleur par index d'employé (pour les avatars)
const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-violet-500',
  'bg-rose-500',
  'bg-amber-500',
  'bg-teal-500',
  'bg-pink-500',
  'bg-indigo-500',
];

// Style visuel selon le statut d'un RDV
const STATUS_STYLE = {
  Confirmé: { bg: 'rgba(219,234,254,0.95)', borderColor: '#2563EB', color: '#1e40af', subColor: '#3b82f6',  dotColor: '#2563EB' },
  Terminé:  { bg: 'rgba(209,250,229,0.95)', borderColor: '#059669', color: '#065f46', subColor: '#10b981',  dotColor: '#059669' },
  Annulé:   { bg: 'rgba(241,245,249,0.95)', borderColor: '#94A3B8', color: '#64748B', subColor: '#94a3b8',  dotColor: '#94A3B8' },
};

const formatDate = (dateStr) =>
  new Date(dateStr + 'T12:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

const isToday = (dateStr) => new Date().toISOString().split('T')[0] === dateStr;

const shiftDay = (dateStr, delta) => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + delta);
  return d.toISOString().split('T')[0];
};

const DURATIONS = [
  { label: '15 min', value: 15  },
  { label: '30 min', value: 30  },
  { label: '45 min', value: 45  },
  { label: '1 h',    value: 60  },
  { label: '1h30',   value: 90  },
  { label: '2 h',    value: 120 },
  { label: '2h30',   value: 150 },
  { label: '3 h',    value: 180 },
  { label: '3h30',   value: 210 },
  { label: '4 h',    value: 240 },
  { label: '4h30',   value: 270 },
  { label: '5 h',    value: 300 },
  { label: '5h30',   value: 330 },
  { label: '6 h',    value: 360 },
];

// ─── Composant ────────────────────────────────────────────────────────────────
export default function TeamCalendar({ employees, bookings, leaves = [], date, onDateChange, onSlotClick, onUpdateStatus, onEditBooking }) {
  const gridRef   = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [slotPopover, setSlotPopover] = useState(null);
  const [mobileEmpIdx, setMobileEmpIdx] = useState(0);

  // Détection taille d'écran en JS (nécessaire pour les largeurs inline du grid)
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;
  const TIME_W   = isMobile ? 44 : 56;

  // Max colonnes selon breakpoint
  const maxCols        = isMobile ? 1 : isTablet ? 3 : employees.length;
  const visibleColCount = Math.min(maxCols, employees.length);
  const needsPagination = employees.length > visibleColCount;

  // Largeur colonne : sur tablette, on remplit l'espace disponible
  const COL_W = isMobile
    ? Math.min(Math.max(windowWidth - 60, 140), 300)
    : isTablet
      ? Math.max(Math.floor((windowWidth - TIME_W - 32) / visibleColCount), 130)
      : 176;

  // Sur mobile/tablette paginée, mobileEmpIdx = index de départ du "viewport"
  const safeIdx = (isMobile || needsPagination)
    ? Math.min(mobileEmpIdx, Math.max(0, employees.length - visibleColCount))
    : 0;
  const visibleEmployees = (isMobile || needsPagination)
    ? employees.slice(safeIdx, safeIdx + visibleColCount).filter(Boolean)
    : employees;

  const dayOfWeek = new Date(date + 'T12:00:00').getDay();

  // Scroll vers l'heure courante si on est sur aujourd'hui
  useEffect(() => {
    if (!gridRef.current || !isToday(date)) return;
    const now     = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    const target  = Math.max(0, ((minutes - DAY_START_MIN) / 30) * SLOT_HEIGHT - 180);
    gridRef.current.scrollTop = target;
  }, [date]);

  // Ligne "heure courante"
  const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();
  const nowTop = isToday(date) ? ((nowMinutes - DAY_START_MIN) / 30) * SLOT_HEIGHT : null;

  // RDV du jour, indexés par employé
  const bookingsByEmp = useMemo(() => {
    const map = {};
    employees.forEach(e => { map[String(e._id)] = []; });
    bookings.forEach(b => {
      if (new Date(b.date).toISOString().split('T')[0] !== date) return;
      const empId = String(b.employeeId?._id || b.employeeId || '');
      if (map[empId]) map[empId].push(b);
    });
    return map;
  }, [bookings, employees, date]);

  // Horaire de travail d'un employé pour ce jour
  // Un jour est "repos" si l'entrée n'existe pas OU si debut/fin sont vides ou identiques
  const getHoraire = (emp) => {
    const h = emp.horaires?.find(h => Number(h.jour) === dayOfWeek);
    if (!h || !h.debut || !h.fin || h.debut === h.fin) return null;
    return h;
  };

  // Congé ? Retourne l'objet congé complet ou null
  const getLeaveInfo = (emp) =>
    leaves.find(l =>
      String(l.employeeId?._id || l.employeeId) === String(emp._id) &&
      date >= new Date(l.dateDebut).toISOString().split('T')[0] &&
      date <= new Date(l.dateFin).toISOString().split('T')[0]
    ) || null;

  return (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden flex flex-col">

      {/* ── Navigation date ── */}
      <div className="px-4 py-3 border-b border-slate-100 shrink-0 space-y-2">
        {/* Ligne 1 : flèches + date */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button onClick={() => onDateChange(shiftDay(date, -1))}
              className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors active:scale-95">
              <ChevronLeft size={18} />
            </button>
            <div className="text-center px-1">
              <p className={`font-black text-xs sm:text-sm capitalize leading-tight ${isToday(date) ? 'text-blue-600' : 'text-slate-800'}`}>
                {formatDate(date)}
              </p>
              {isToday(date) && (
                <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">Aujourd'hui</span>
              )}
            </div>
            <button onClick={() => onDateChange(shiftDay(date, 1))}
              className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors active:scale-95">
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Aujourd'hui + date picker */}
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => onDateChange(new Date().toISOString().split('T')[0])}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-600 rounded-xl font-bold text-xs hover:bg-blue-100 transition-all active:scale-95 whitespace-nowrap">
              <CalendarDays size={13} />
              <span className="hidden sm:inline">Aujourd'hui</span>
            </button>
            <input
              type="date"
              value={date}
              onChange={e => e.target.value && onDateChange(e.target.value)}
              className="px-2 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 outline-none focus:ring-2 ring-blue-500/20 cursor-pointer"
            />
          </div>
        </div>

        {/* Ligne 2 : sélecteur d'employé - mobile : nav compacte, tablette paginée : pilules */}
        {(isMobile || needsPagination) && employees.length > 0 && (
          isMobile ? (
            /* Mobile : ‹ [Employé courant] › + compteur */
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMobileEmpIdx(Math.max(0, safeIdx - 1))}
                disabled={safeIdx === 0}
                className="p-2 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-25 shrink-0 transition-all active:scale-95"
              >
                <ChevronLeft size={16} />
              </button>

              <div className="flex-1 flex items-center gap-2.5 bg-blue-600 text-white px-3 py-2 rounded-2xl shadow-md justify-center">
                {(() => {
                  const emp      = employees[safeIdx];
                  const leaveInfo = emp ? getLeaveInfo(emp) : null;
                  const horaire   = emp ? getHoraire(emp) : null;
                  const initials  = emp ? `${emp.prenom?.[0] || ''}${emp.nom?.[0] || ''}`.toUpperCase() : '';
                  return (
                    <>
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center font-black text-[10px] bg-white/20 shrink-0">
                        {initials}
                      </div>
                      <span className="font-bold text-sm truncate">{emp?.prenom} {emp?.nom}</span>
                      {(leaveInfo || !horaire) && (
                        <span className="text-blue-200 text-[11px]">{leaveInfo ? '🌴' : '😴'}</span>
                      )}
                    </>
                  );
                })()}
              </div>

              <button
                onClick={() => setMobileEmpIdx(Math.min(employees.length - 1, safeIdx + 1))}
                disabled={safeIdx >= employees.length - 1}
                className="p-2 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-25 shrink-0 transition-all active:scale-95"
              >
                <ChevronRight size={16} />
              </button>

              <span className="text-[10px] font-bold text-slate-400 shrink-0">{safeIdx + 1}/{employees.length}</span>
            </div>
          ) : (
            /* Tablette paginée : pilules + flèches de groupe */
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setMobileEmpIdx(Math.max(0, safeIdx - visibleColCount))}
                disabled={safeIdx === 0}
                className="p-1.5 rounded-xl border border-slate-200 text-slate-400 hover:bg-slate-50 disabled:opacity-30 shrink-0 transition-all"
              >
                <ChevronLeft size={15} />
              </button>

              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 flex-1">
                {employees.map((emp, idx) => {
                  const leaveInfo  = getLeaveInfo(emp);
                  const leave      = !!leaveInfo;
                  const horaire    = getHoraire(emp);
                  const initials   = `${emp.prenom?.[0] || ''}${emp.nom?.[0] || ''}`.toUpperCase();
                  const isInView   = idx >= safeIdx && idx < safeIdx + visibleColCount;

                  return (
                    <button
                      key={emp._id}
                      onClick={() => setMobileEmpIdx(Math.min(idx, Math.max(0, employees.length - visibleColCount)))}
                      className={`flex items-center gap-2 px-3 py-2 rounded-2xl border-2 whitespace-nowrap transition-all shrink-0 ${
                        isInView
                          ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                          : 'bg-white border-slate-100 text-slate-600 hover:border-slate-200'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center font-black text-[10px] shrink-0 ${
                        isInView ? 'bg-white/20 text-white' : `${AVATAR_COLORS[idx % AVATAR_COLORS.length]} text-white`
                      }`}>
                        {initials}
                      </div>
                      <span className="font-bold text-xs">{emp.prenom}</span>
                      {(leave || !horaire) && (
                        <span className={`text-[9px] font-black ${isInView ? 'text-blue-200' : 'text-slate-400'}`}>
                          {leave ? '🌴' : '😴'}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setMobileEmpIdx(Math.min(employees.length - visibleColCount, safeIdx + visibleColCount))}
                disabled={safeIdx + visibleColCount >= employees.length}
                className="p-1.5 rounded-xl border border-slate-200 text-slate-400 hover:bg-slate-50 disabled:opacity-30 shrink-0 transition-all"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          )
        )}
      </div>

      {/* ── Grille ── */}
      <div ref={gridRef} className="overflow-auto" style={{ maxHeight: '72vh' }}>
        <div style={{ minWidth: TIME_W + COL_W * visibleEmployees.length + 'px' }}>

          {/* ── Header employés (sticky) ── */}
          <div className="sticky top-0 z-20 bg-white/98 backdrop-blur-sm border-b border-slate-100 flex"
            style={{ paddingLeft: TIME_W + 'px' }}>
            {visibleEmployees.map((emp, idx) => {
              const realIdx   = (isMobile || needsPagination) ? safeIdx + idx : idx;
              const horaire   = getHoraire(emp);
              const leaveInfo = getLeaveInfo(emp);
              const leave     = !!leaveInfo;
              const leaveType = leaveInfo?.type || 'Congé';
              const leaveCfg  = LEAVE_CONFIG[leaveType] || LEAVE_CONFIG['Congé'];
              const empBkgs   = bookingsByEmp[String(emp._id)] || [];
              const confirmed = empBkgs.filter(b => b.status === 'Confirmé').length;
              const done      = empBkgs.filter(b => b.status === 'Terminé').length;
              const initials  = `${emp.prenom?.[0] || ''}${emp.nom?.[0] || ''}`.toUpperCase();
              const avatarColor = leave || !horaire ? '#E2E8F0' : AVATAR_COLORS[realIdx % AVATAR_COLORS.length].replace('bg-', '');

              return (
                <div key={emp._id}
                  style={{ width: COL_W + 'px', minWidth: COL_W + 'px' }}
                  className="border-l border-slate-100 px-2 py-2.5 flex flex-col items-center gap-1">

                  {/* Avatar */}
                  <div className={`w-9 h-9 rounded-xl items-center justify-center font-black text-sm hidden sm:flex ${
                    leave || !horaire
                      ? 'bg-slate-100 text-slate-300'
                      : `${AVATAR_COLORS[realIdx % AVATAR_COLORS.length]} text-white`
                  }`}>
                    {initials}
                  </div>

                  {/* Nom */}
                  <p className="font-black text-[11px] text-slate-700 truncate max-w-full leading-none text-center">
                    {emp.prenom}
                  </p>
                  <p className="text-[9px] text-slate-400 font-bold truncate max-w-full text-center hidden sm:block">{emp.nom}</p>

                  {/* Statut du jour */}
                  {leave ? (
                    <span className={`px-2 py-0.5 text-[8px] font-black uppercase rounded-full ${leaveCfg.badge}`}>
                      {leaveCfg.label}
                    </span>
                  ) : !horaire ? (
                    <span className="px-2 py-0.5 bg-slate-50 text-slate-300 text-[8px] font-black uppercase rounded-full tracking-wide">
                      Repos
                    </span>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      {confirmed > 0 && (
                        <span className="flex items-center gap-0.5 text-[9px] font-bold text-blue-500">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />{confirmed}
                        </span>
                      )}
                      {done > 0 && (
                        <span className="flex items-center gap-0.5 text-[9px] font-bold text-emerald-500">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />{done}
                        </span>
                      )}
                      {confirmed === 0 && done === 0 && (
                        <span className="text-[9px] font-bold text-slate-300">Libre</span>
                      )}
                    </div>
                  )}

                  {/* Horaire */}
                  {horaire && !leave && (
                    <p className="text-[8px] text-slate-300 font-bold hidden sm:block">
                      {horaire.debut}–{horaire.fin}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Corps : heures + colonnes ── */}
          <div className="relative flex">

            {/* Colonne heures */}
            <div className="shrink-0 select-none" style={{ width: TIME_W + 'px' }}>
              {SLOT_TIMES.map((slot, i) => (
                <div key={slot} style={{ height: SLOT_HEIGHT + 'px' }}
                  className="flex items-start justify-end pr-3 pt-2">
                  {i % 2 === 0 && (
                    <span className={`text-[10px] font-bold leading-none ${
                      isToday(date) && timeToMinutes(slot) <= nowMinutes && nowMinutes < timeToMinutes(slot) + 60
                        ? 'text-red-400'
                        : 'text-slate-300'
                    }`}>
                      {slot}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Colonne par employé */}
            {visibleEmployees.map((emp, idx) => {
              const realIdx    = (isMobile || needsPagination) ? safeIdx + idx : idx;
              const horaire    = getHoraire(emp);
              const leaveInfo  = getLeaveInfo(emp);
              const leave      = !!leaveInfo;
              const leaveType  = leaveInfo?.type || 'Congé';
              const leaveCfg   = LEAVE_CONFIG[leaveType] || LEAVE_CONFIG['Congé'];
              const workStart  = horaire ? timeToMinutes(horaire.debut) : null;
              const workEnd    = horaire ? timeToMinutes(horaire.fin)   : null;
              const empBookings = bookingsByEmp[String(emp._id)] || [];

              // Toute la journée indisponible ?
              const fullyUnavailable = leave || !horaire;

              return (
                <div key={emp._id}
                  style={{ width: COL_W + 'px', minWidth: COL_W + 'px' }}
                  className="relative border-l border-slate-100">

                  {/* Fond des créneaux */}
                  {SLOT_TIMES.map((slot, i) => {
                    const slotMin    = timeToMinutes(slot);
                    const inWork     = !fullyUnavailable && slotMin >= workStart && slotMin < workEnd;
                    const isHov      = hovered?.empId === String(emp._id) && hovered?.slot === slot;
                    const isHourLine = i % 2 === 0;

                    return (
                      <div key={slot}
                        style={{ height: SLOT_HEIGHT + 'px' }}
                        className={`relative border-b transition-colors ${
                          isHourLine ? 'border-slate-100' : 'border-slate-50'
                        } ${
                          inWork
                            ? isHov
                              ? 'bg-blue-50 cursor-pointer'
                              : 'bg-white hover:bg-blue-50/40 cursor-pointer'
                            : fullyUnavailable
                              ? leave ? leaveCfg.bg : 'bg-slate-50/80'
                              : 'bg-slate-50/70'
                        }`}
                        onMouseEnter={() => inWork && setHovered({ empId: String(emp._id), slot })}
                        onMouseLeave={() => setHovered(null)}
                        onClick={(e) => {
                          if (!inWork) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          setSlotPopover({
                            emp, date, slot,
                            x: Math.min(rect.left, window.innerWidth - 240),
                            y: rect.top + rect.height / 2,
                          });
                        }}
                      >
                        {/* Indicateur "+" au survol */}
                        {isHov && inWork && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="flex items-center gap-1 text-blue-400 text-[10px] font-black bg-white/80 px-2 py-1 rounded-lg shadow-sm backdrop-blur-sm">
                              <Plus size={11} /> Nouveau RDV
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Overlay Repos / Congé centré dans la colonne */}
                  {fullyUnavailable && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10 gap-2">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-xl ${
                        leave ? leaveCfg.badge.split(' ')[0] : 'bg-slate-100'
                      }`}>
                        {leave ? leaveCfg.icon : '😴'}
                      </div>
                      <span className={`text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${
                        leave ? leaveCfg.badge : 'bg-slate-100 text-slate-400'
                      }`}>
                        {leave ? leaveCfg.label : 'Jour de repos'}
                      </span>
                    </div>
                  )}

                  {/* Blocs RDV (positionnement absolu) */}
                  {empBookings.map(booking => {
                    const startMin = timeToMinutes(booking.heureDebut);
                    const endMin   = timeToMinutes(booking.heureFin);
                    const top      = ((startMin - DAY_START_MIN) / 30) * SLOT_HEIGHT;
                    const height   = Math.max(((endMin - startMin) / 30) * SLOT_HEIGHT, 28);
                    const st       = STATUS_STYLE[booking.status] || STATUS_STYLE['Confirmé'];
                    const compact  = height < SLOT_HEIGHT;

                    return (
                      <div
                        key={booking._id}
                        style={{
                          top:         top + 3 + 'px',
                          height:      height - 6 + 'px',
                          left:        '5px',
                          right:       '5px',
                          background:  st.bg,
                          borderLeft:  `3px solid ${st.borderColor}`,
                          borderRadius: '10px',
                        }}
                        className="absolute z-10 px-2 py-1.5 overflow-hidden cursor-pointer transition-all duration-150 hover:scale-[1.02] hover:shadow-md"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTooltip(prev => prev?._id === booking._id ? null : booking);
                        }}
                      >
                        <p className="font-black truncate leading-tight"
                          style={{ fontSize: compact ? '10px' : '11px', color: st.color }}>
                          {[booking.clientPrenom, booking.clientNom].filter(Boolean).join(' ')}
                        </p>
                        {!compact && (
                          <p className="text-[9px] font-bold truncate mt-0.5"
                            style={{ color: st.subColor }}>
                            {booking.heureDebut}–{booking.heureFin}
                            {booking.service ? ` · ${booking.service}` : ''}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* ── Ligne heure courante ── */}
            {nowTop !== null && nowTop >= 0 && (
              <div
                className="absolute left-0 right-0 z-30 pointer-events-none flex items-center"
                style={{ top: nowTop + 'px' }}>
                <div style={{ width: TIME_W + 'px' }} className="flex justify-end pr-1.5 shrink-0">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-md shadow-red-200" />
                </div>
                <div className="flex-1 h-px bg-red-400 opacity-80" />
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── Légende ── */}
      <div className="px-4 py-2.5 border-t border-slate-100 flex items-center gap-4 text-[10px] font-bold text-slate-400 shrink-0 flex-wrap">
        {[
          { label: 'Confirmé', color: '#2563EB', bg: 'rgba(219,234,254,0.95)' },
          { label: 'Terminé',  color: '#059669', bg: 'rgba(209,250,229,0.95)' },
          { label: 'Annulé',   color: '#94A3B8', bg: 'rgba(241,245,249,0.95)' },
        ].map(({ label, color, bg }) => (
          <span key={label} className="flex items-center gap-1.5">
            <span style={{ width: 12, height: 12, borderRadius: 3, background: bg, borderLeft: `3px solid ${color}`, display: 'inline-block' }} />
            <span style={{ color }}>{label}</span>
          </span>
        ))}
        <span className="hidden md:flex items-center gap-1 ml-auto text-slate-300 font-medium">
          Appuyer sur un créneau libre pour créer un RDV
        </span>
      </div>

      {/* ── Popover sélection durée ── */}
      {slotPopover && (
        <>
          {/* Backdrop invisible pour fermer au clic extérieur */}
          <div className="fixed inset-0 z-40" onClick={() => setSlotPopover(null)} />

          <div
            className={`fixed z-50 bg-white shadow-2xl border border-slate-100 overflow-hidden animate-in duration-150 ${
              isMobile
                ? 'rounded-t-[2rem] bottom-0 left-0 right-0 w-full fade-in slide-in-from-bottom'
                : 'rounded-2xl w-56 zoom-in-95 fade-in'
            }`}
            style={isMobile ? {} : {
              left: slotPopover.x + 'px',
              top:  slotPopover.y + 'px',
              transform: 'translateY(-50%)',
            }}
          >
            {/* En-tête */}
            <div className="px-4 pt-3.5 pb-2 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="font-black text-slate-800 text-xs">{slotPopover.emp.prenom}</p>
                <p className="flex items-center gap-1 text-[10px] font-bold text-blue-500 mt-0.5">
                  <Clock size={10} /> {slotPopover.slot}
                </p>
              </div>
              <button onClick={() => setSlotPopover(null)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
                <X size={13} />
              </button>
            </div>

            {/* Durées */}
            <div className="p-2">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-2 pb-1.5">Durée de la prestation</p>
              <div className="grid grid-cols-2 gap-1">
                {DURATIONS.map(d => {
                  const endMin  = timeToMinutes(slotPopover.slot) + d.value;
                  const endTime = minutesToTime(Math.min(endMin, DAY_END_MIN));
                  return (
                    <button
                      key={d.value}
                      onClick={() => {
                        setSlotPopover(null);
                        setHovered(null);
                        if (onSlotClick) onSlotClick(slotPopover.emp, slotPopover.date, slotPopover.slot, d.value);
                      }}
                      className="flex flex-col items-start px-3 py-2 rounded-xl hover:bg-blue-50 hover:text-blue-700 text-left transition-colors group"
                    >
                      <span className="font-black text-[11px] text-slate-800 group-hover:text-blue-700">{d.label}</span>
                      <span className="text-[9px] text-slate-400 group-hover:text-blue-400 font-medium">
                        → {endTime}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Tooltip détail RDV ── */}
      {tooltip && (() => {
        const st = STATUS_STYLE[tooltip.status] || STATUS_STYLE['Confirmé'];
        return (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30 backdrop-blur-sm"
            onClick={() => setTooltip(null)}
          >
            <div
              className="bg-white rounded-t-[2rem] sm:rounded-[2rem] w-full max-w-sm shadow-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Barre statut */}
              <div className="h-1 w-full" style={{ background: st.borderColor }} />

              <div className="p-5">
                {/* En-tête */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <span className="px-2.5 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest border"
                      style={{ background: st.bg, borderColor: st.borderColor + '44', color: st.color }}>
                      {tooltip.status}
                    </span>
                    <h3 className="text-lg font-black text-slate-900 leading-tight mt-2">
                      {[tooltip.clientPrenom, tooltip.clientNom].filter(Boolean).join(' ')}
                    </h3>
                    {tooltip.service && (
                      <p className="text-sm font-medium mt-0.5" style={{ color: st.subColor }}>{tooltip.service}</p>
                    )}
                  </div>
                  <button onClick={() => setTooltip(null)}
                    className="shrink-0 w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-400 transition-colors">
                    <X size={16} />
                  </button>
                </div>

                {/* Infos */}
                <div className="rounded-2xl border border-slate-100 bg-slate-50 divide-y divide-slate-100 mb-4">
                  <Row label="Horaire"   value={`${tooltip.heureDebut} – ${tooltip.heureFin}`} />
                  <Row label="Coiffeur"  value={tooltip.employeeId ? `${tooltip.employeeId.prenom || ''} ${tooltip.employeeId.nom || ''}`.trim() : 'Non assigné'} />
                  {tooltip.clientTelephone && <Row label="Téléphone" value={tooltip.clientTelephone} />}
                  {tooltip.prix > 0      && <Row label="Prix"      value={tooltip.prix?.toLocaleString()} />}
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  {tooltip.status === 'Confirmé' && (
                    <>
                      <div className="flex gap-2">
                        <button onClick={() => { onUpdateStatus(tooltip._id, 'Terminé'); setTooltip(null); }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-emerald-500 text-white rounded-xl font-bold text-xs shadow-sm hover:bg-emerald-600 transition-all active:scale-95">
                          <CheckCircle size={13} /> Terminer
                        </button>
                        <button onClick={() => { onUpdateStatus(tooltip._id, 'Annulé'); setTooltip(null); }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-red-100 text-red-500 rounded-xl font-bold text-xs hover:bg-red-50 transition-all active:scale-95">
                          <XCircle size={13} /> Annuler
                        </button>
                      </div>
                      <button onClick={() => { onEditBooking(tooltip); setTooltip(null); }}
                        className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-slate-50 text-slate-600 border border-slate-200 rounded-xl font-bold text-xs hover:bg-slate-100 transition-all active:scale-95">
                        <Pencil size={13} /> Modifier ce RDV
                      </button>
                    </>
                  )}
                  {tooltip.status === 'Annulé' && (
                    <>
                      <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest text-center">Réactiver ce RDV</p>
                      <div className="flex gap-2">
                        <button onClick={() => { onUpdateStatus(tooltip._id, 'Confirmé'); setTooltip(null); }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-blue-50 text-blue-600 border border-blue-100 rounded-xl font-bold text-xs hover:bg-blue-100 transition-all active:scale-95">
                          Confirmé
                        </button>
                        <button onClick={() => { onUpdateStatus(tooltip._id, 'Terminé'); setTooltip(null); }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-xl font-bold text-xs hover:bg-emerald-100 transition-all active:scale-95">
                          Terminé
                        </button>
                      </div>
                      <button onClick={() => { onEditBooking(tooltip); setTooltip(null); }}
                        className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-slate-50 text-slate-600 border border-slate-200 rounded-xl font-bold text-xs hover:bg-slate-100 transition-all active:scale-95">
                        <Pencil size={13} /> Modifier ce RDV
                      </button>
                    </>
                  )}
                  {tooltip.status === 'Terminé' && (
                    <button onClick={() => setTooltip(null)}
                      className="w-full py-2.5 bg-slate-50 text-slate-500 border border-slate-200 rounded-xl font-bold text-xs hover:bg-slate-100 transition-all">
                      Fermer
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Ligne label / valeur dans le tooltip
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2">
      <span className="text-slate-400 text-xs font-semibold shrink-0">{label}</span>
      <span className="font-bold text-slate-700 text-xs text-right">{value}</span>
    </div>
  );
}
